import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SecurityContext,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { DocumentsService } from '@services/documents.service';
import { CompaniesService } from '@services/companies.service';
import { CompanyModel } from '@data-layer/models/companies';
import { EntrySummaryModel, InvoiceModel, LineModel } from '@data-layer/models/entry-summaries';
import { DateHelper } from '@helpers/date.helper';
import { filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { EntrySummaryService } from '@services/entry-summary.service';
import { DocumentModel } from '@data-layer/models/documents';
import { CurrencyModel, IncoTermModel, ValidationMessageModel } from '@data-layer/models/common';
import { CodeRepository, CurrenciesRepository } from '@data-layer/repositories';
import { enumTransportationMode } from '@data-layer/enums/entry-summaries';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { CustomValidator } from '@helpers/custom-validator';
import { ALLOWED_FILE_EXTENSIONS, ALLOWED_FILE_MIMES } from '@constants/constants';
import { environment } from '@env/environment';
import { EntrySummaryStateService } from '@shared/es-mutual/services';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { enumDocumentRelatedEntityType, enumDocumentRootEntityType, enumDocumentType } from '@data-layer/enums/documents';
import { enumIncotermApplicableType, enumOcrStatus } from '@data-layer/enums/common';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { UserBetaFlagModel } from '@app/data-layer/models/user';

@Component({
  selector: 'es-details-invoice-ocr-card-desktop',
  templateUrl: './es-details-invoice-ocr-card-desktop.component.html',
  styleUrls: ['./es-details-invoice-ocr-card-desktop.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsDetailsInvoiceOcrCardDesktopComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public entrySummary: EntrySummaryModel;
  @Input() public referenceNumber: string;
  @Input() public invoice: InvoiceModel;
  @Input() public billOfLadingId: number;
  @Input() public esId: number;
  @Input() public isBatchES: boolean;
  @Input() public transportationMode: enumTransportationMode;
  @Input() public attachedDocuments: DocumentModel[] = [];
  @Input() public isCreatingInvoice: boolean;
  @Input() public isEditable: boolean = true;
  // Additive granular edit affordances forwarded to the main card (broker page). Fall back to
  // `isEditable` when undefined → user-side / admin unchanged.
  @Input() public canEditInvoiceDetails: boolean;
  @Input() public canDeleteInvoice: boolean;
  @Input() public canEditLineItems: boolean;
  @Input() public canClearInvoice: boolean;
  @Input() public canEditManufacturer: boolean;
  @Input() public shipmentExportDate: string;
  @Input() public currencyRate: number;
  @Input() public currencies: CurrencyModel[] = [];
  @Input() public incoterms: IncoTermModel[] = [];
  @Input() public invoiceFg: UntypedFormGroup;
  // Broker review page: force the inline manual-entry form open after a parent-driven "Clear data"
  // (the broker takes the `canClearInvoice` early-return in clearData, so the internal path that
  // would set `isFormOpened` never runs). Unbound (undefined) → user-side unaffected.
  @Input() public isFormForcedOpen: boolean;
  // Gate for the line items multi-select / bulk-delete UI (client + broker pages); admin leaves it unbound → false.
  @Input() public isCanBulkDelete: boolean = false;
  // #13384 "Finalize" -> Validate HTS codes view. Bound by the client page only; broker/admin leave it unbound -> hidden.
  // Hidden once every line of the Bill of Lading is verified; disabled on an entry the importer cannot edit.
  @Input() public isShowFinalize: boolean;
  @Input() public isFinalizeEnabled: boolean;
  // #13499 — ordinal of this invoice inside its BL; bound only when the BL carries 2+ invoices, null/unbound → no title.
  @Input() public invoiceOrdinal: number;
  @Output() public editInvoice: EventEmitter<void> = new EventEmitter<void>();
  @Output() public updateInvoice: EventEmitter<void> = new EventEmitter<void>();
  @Output() public editMF: EventEmitter<void> = new EventEmitter<void>();
  @Output() public editCleanMF: EventEmitter<InvoiceModel> = new EventEmitter<InvoiceModel>();
  @Output() public addLineItem: EventEmitter<void> = new EventEmitter<void>();
  @Output() public replaceInvoice: EventEmitter<File> = new EventEmitter<File>();
  @Output() public clearInvoice: EventEmitter<void> = new EventEmitter<void>();
  // Emitted INSTEAD of the internal clean-invoice call when `canClearInvoice` is bound true
  // (broker review page) — the card sits in the page template, so its injected state service is
  // the user-side root one; the broker parent must run the clear through its own service.
  @Output() public clearDataRequested: EventEmitter<void> = new EventEmitter<void>();
  @Output() public deleteInvoice: EventEmitter<void> = new EventEmitter<void>();
  @Output() public editLineItem: EventEmitter<number> = new EventEmitter();
  @Output() public deleteLineItem: EventEmitter<number> = new EventEmitter();
  @Output() public linesDeleted: EventEmitter<void> = new EventEmitter<void>();
  @Output() public save: EventEmitter<number> = new EventEmitter();
  @Output() public htsOpenSource: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public fileUploaded: EventEmitter<void> = new EventEmitter<void>();
  @Output() public reviewInvoice: EventEmitter<void> = new EventEmitter<void>();
  @Output() public finalize: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('link', { static: false })
  public linkRef: ElementRef;
  @ViewChild('inputElement', { static: false }) public inputElement: ElementRef;

  public company: CompanyModel;
  public isOpenedItems: boolean;
  public isDuplicate: boolean;
  public isShowReviewHeader: boolean = true;
  public isShowClearHeader: boolean = true;
  public isShowClearInvoiceConfirm: boolean;
  public totalEnteredValue: number;
  public totalEnteredValueInUSD: number;
  public totalGrossWeight: number;
  public previewImgUrl: string;
  public previewImgUrlForBg: string;
  public incotermsByType: IncoTermModel[] = [];

  public isInvoiceLinesCorrect: boolean;
  public validationMessages: ValidationMessageModel[] = [];
  public fileControl: UntypedFormControl;
  public allowedFileMimes: typeof ALLOWED_FILE_MIMES = ALLOWED_FILE_MIMES;
  public enumDocumentRootEntityType: typeof enumDocumentRootEntityType = enumDocumentRootEntityType;
  public isFormOpened: boolean;
  public isPreviewModalOpened: boolean;
  public isLoadingThumb: boolean;
  public isLoadingReplacedThumb: boolean;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private documentsService: DocumentsService,
    private companiesService: CompaniesService,
    private entrySummaryStateService: EntrySummaryStateService,
    private entrySummaryService: EntrySummaryService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private codeRepository: CodeRepository,
    public sanitizer: DomSanitizer,
  ) {}

  public ngOnInit(): void {
    const params = this.route.snapshot.queryParams;
    this.isDuplicate = !!params?.isDuplicate;
    this.company = this.companiesService.getCompany();
    if (!this.invoice?.currency && this.isHasEsOcrRedesign) {
      this.invoice.currency = DEFAULT_CURRENCY_CODE;
    }

    this.fileControl = new UntypedFormControl(null, [
      CustomValidator.fileExtensions(ALLOWED_FILE_EXTENSIONS),
      CustomValidator.maxFileSize(environment.files.default_file_size_limit),
    ]);

    this.checkData();

    this.isLoadingThumb = true;
    this.entrySummaryStateService.entryDocuments$
      .pipe(
        filter((docs: DocumentModel[]) => docs?.length && !this.previewImgUrl),
        switchMap(() => {
          return this.entrySummaryStateService.getThumbLink(this.invoice.id, enumDocumentType.FILE_PREVIEW, enumDocumentType.INVOICE);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(
        (data: { downloadUrl: string }) => {
          if (data?.downloadUrl) {
            this.previewImgUrl = data.downloadUrl || null;
            this.previewImgUrlForBg = this.sanitizer.sanitize(SecurityContext.STYLE, `url("${this.previewImgUrl}")`) || null;
          } else {
            this.previewImgUrl = null;
            this.previewImgUrlForBg = null;
          }
          this.cdr.markForCheck();
          this.isLoadingThumb = false;
        },
        () => (this.isLoadingThumb = false),
      );

    if (
      [enumOcrStatus.EXTRACTION_FAILED, enumOcrStatus.CANCELED].includes(this.invoice?.ocrStatus) &&
      !this.invoice?.invoiceNumber &&
      !this.invoice?.countryOfExportId &&
      !this.invoice?.manufacturerId
    ) {
      this.isFormOpened = true;
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.invoice && !changes.invoice?.isFirstChange()) {
      this.checkData();
    }

    if (changes.isCreatingInvoice?.currentValue && !this.isHasEsOcrRedesign) {
      this.closeItems();
    }

    // Broker review page forces the inline form open after a parent-driven clear (force-open only;
    // saveInvoice() handles closing it).
    if (changes.isFormForcedOpen?.currentValue === true) {
      this.isFormOpened = true;
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public openItems(): void {
    this.isOpenedItems = true;
  }

  public closeItems(): void {
    this.isOpenedItems = false;
  }

  public showInvoice(): void {
    if (!this.previewImgUrl) {
      return;
    }
    this.openPreviewModal();
  }

  public onReplaceInvoice() {
    if (this.isHasEsOcrRedesign) {
      this.addDocStripInvoice(this.billOfLadingId);
    } else {
      this.inputElement.nativeElement.click();
    }
  }

  public addDocStripInvoice(shipmentId: number) {
    const docTypesToSkip = [enumDocumentType.FILE_PREVIEW];

    const currentInvoiceDocument = this.attachedDocuments.find(
      (doc) => doc.type === enumDocumentType.INVOICE && doc.relatedEntityId === this.invoice?.id && !docTypesToSkip.includes(doc.type),
    );

    const data = {
      document: {
        id: !!currentInvoiceDocument ? currentInvoiceDocument.id : -1, //default invoice id
        rootEntityId: this.entrySummary.id,
        rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
        relatedEntityId: this.invoice?.id,
        relatedEntityType: enumDocumentRelatedEntityType.INVOICE,
        url: null,
        createdDate: null,
        fileName: null,
        type: enumDocumentType.INVOICE,
        state: null,
      },
      shipmentId: shipmentId,
      isForceAdd: true,
      isInvoiceReplace: true,
    };

    this.entrySummaryStateService.uploadDocStripDocument(data);
  }

  public downloadInvoice(): void {
    const file: DocumentModel = this.attachedDocuments.find(
      (item: DocumentModel) => item.relatedEntityId === this.invoice?.id && item.type === enumDocumentType.INVOICE,
    );
    if (!!file) {
      this.getDownloadLink(file.id)
        .pipe(take(1))
        .subscribe((linkData: { downloadUrl: string }) => {
          this.linkRef.nativeElement.href = linkData.downloadUrl;
          this.linkRef.nativeElement.click();
        });
    }
  }

  public saveInvoiceHTS(): void {
    if (!this.isHasEsOcrRedesign) {
      this.closeItems();
      this.isFormOpened = false;
      this.htsOpenSource.emit(this.isOpenedItems);
    }

    this.save.emit();
  }

  public saveInvoice(): void {
    this.isFormOpened = false;
    this.updateInvoice.emit();
  }

  public clickInput(): void {
    this.clearFile();
  }

  public handleFiles(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    const files: FileList = target.files;
    if (files?.length) {
      this.uploadFile(files[0]);
    }
  }

  public uploadFile(file: File): void {
    if (file) {
      const reader: FileReader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event: ProgressEvent<FileReader>) => {
        this.clearFile();
        this.fileControl.patchValue(file);
        this.fileControl.markAsTouched();

        this.inputElement.nativeElement.value = null;
        if (this.fileControl.valid) {
          this.replaceInvoice.emit(file);
        }

        this.cdr.detectChanges();
      };
    }
  }

  public clearData(): void {
    this.onHideClearInvoiceConfirm();

    // Broker review page: delegate the clear to the parent (see clearDataRequested doc).
    // The confirm-dialog UX above runs for both paths.
    if (this.canClearInvoice === true) {
      this.clearDataRequested.emit();
      return;
    }

    this.entrySummaryStateService
      .cleanInvoice(this.invoice.id, {
        ...this.invoice,
        invoiceNumber: null,
        currency: DEFAULT_CURRENCY_CODE,
        incoTermsCode: null,
        manufacturerId: null,
        manufacturer: this.invoice.manufacturer,
      })
      .pipe(
        switchMap(() => {
          return this.entrySummaryStateService.loadEntrySummary(this.referenceNumber);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((entry: EntrySummaryModel) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        this.clearInvoice.emit();
        this.isFormOpened = true;
        this.cdr.markForCheck();
      });
  }

  public openPreviewModal(): void {
    this.isPreviewModalOpened = true;
  }

  public closePreviewModal(): void {
    this.isPreviewModalOpened = false;
  }

  public detectChanges(): void {
    this.cdr.markForCheck();
  }

  public isImagePreview(downloadUrl: string): boolean {
    return this.documentsService.checkImageFileExtension(downloadUrl);
  }

  public onShowClearInvoiceConfirm(): void {
    this.isShowClearInvoiceConfirm = true;
  }

  public onHideClearInvoiceConfirm(): void {
    this.isShowClearInvoiceConfirm = false;
  }

  public setLoadingReplacedThumb(val: boolean): void {
    this.isLoadingReplacedThumb = val;
  }

  public showClearInvoice(): void {
    if (this.isShowError) {
      this.onShowClearInvoiceConfirm();
    } else {
      this.clearData();
    }
  }

  public get isShowInvoiceOrdinal(): boolean {
    return this.invoiceOrdinal > 0;
  }

  public get isSupportOrAdmin(): boolean {
    return this.companiesService.isSupportOrAdmin();
  }

  public get errorText(): string {
    return this.isOpenedItems || this.isShowSave
      ? 'OCR_MANUALLY_ENTER_ANY_MISSING_DETAILS'
      : 'OCR_MANUALLY_ENTER_ANY_MISSING_DETAILS_CLOSED';
  }

  public get isShowError(): boolean {
    return (
      !this.isFormOpened &&
      (!!(this.validationMessages || [])?.filter((item: ValidationMessageModel) => item.text !== 'INVOICE_OCR_STATE_NOT_REVIEWED')
        ?.length ||
        !this.isInvoiceLinesCorrect) &&
      this.invoice.ocrStatus === enumOcrStatus.PARTIALLY_PROCESSED
    );
  }

  public get isShowSave(): boolean {
    const isWithValidationMessages = !!(this.validationMessages || [])?.filter(
      (item: ValidationMessageModel) => item.text !== 'INVOICE_OCR_STATE_NOT_REVIEWED',
    )?.length;
    return !isWithValidationMessages && this.isInvoiceLinesCorrect && !!this.invoice?.lines?.length && !this.isShowError;
  }

  public get isShowCollapseHeader(): boolean {
    if (this.isOpenedItems) {
      return false;
    }

    return this.isShowCollapse;
  }

  public get isShowCollapse(): boolean {
    return !this.isHasEsOcrRedesign;
  }

  public get isShowFinalizeButton(): boolean {
    return (
      this.isShowFinalize === true &&
      this.isFinalizeEnabled === true &&
      this.companiesService.hasUserBetaFlag(enumUserBetaFlag.htsClassificationAssistant)
    );
  }

  public get isFinalizeDisabled(): boolean {
    return !this.isEditable;
  }

  /** "Save invoice" (the assistant's Validate view) takes the primary look; "Add another line item" steps back to outline. */
  public get isAddLineItemOutline(): boolean {
    return this.isShowFinalizeButton;
  }

  public get isHasEsOcrRedesign(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.esOcrRedesign);
  }

  private getTotalEnteredValue(): number {
    return (this.invoice?.lines || []).reduce((acc: number, curr: LineModel) => acc + curr.value, 0);
  }

  private getTotalGrossWeight(): number {
    return (this.invoice?.lines || []).reduce((acc: number, curr: LineModel) => acc + curr.grossWeight, 0);
  }

  private getDownloadLink(fileId: number): Observable<{ downloadUrl: string }> {
    if (!!fileId) {
      return this.documentsService.getDownloadLink(fileId);
    }
  }

  private checkInvoiceLinesValidation(): void {
    const grossWeightMessage: string = 'GROSS_WEIGHT_MISSING';

    this.isInvoiceLinesCorrect = (this.invoice.lines || []).every((line: LineModel) => {
      return line.validationMessages.filter((lineItem: ValidationMessageModel) => lineItem.text !== grossWeightMessage).length === 0;
    });

    if ((!this.isInvoiceLinesCorrect && !this.isCreatingInvoice) || this.isDuplicate || this.isHasEsOcrRedesign) {
      this.openItems();
      //#10823
      if (this.isDuplicate) {
        this.removeQueryParams(['isDuplicate']);
      }
    }
  }

  private checkValidation(): void {
    this.validationMessages = [
      ...this.invoice?.validationMessages,
      ...(this.invoice?.manufacturer?.validationMessages || []),
      ...Object.values(this.invoice?.manufacturer?.address?.validationMessages || []).map((message: any) => message?.text),
    ];
  }

  private checkData(): void {
    this.totalEnteredValue = this.getTotalEnteredValue();
    this.totalGrossWeight = this.getTotalGrossWeight();
    this.entrySummaryService
      .getInvoiceEnteredValueInDefaultCurrency(this.invoice, this.shipmentExportDate ? this.shipmentExportDate : DateHelper.now)
      .pipe(take(1))
      .subscribe((total: number) => {
        this.totalEnteredValueInUSD = total;
        this.cdr.markForCheck();
      });

    this.checkValidation();
    this.checkInvoiceLinesValidation();

    if (!this.invoice?.lines?.length) {
      this.openItems();
    }

    this.getIncoterms();
  }

  private clearFile(): void {
    this.fileControl.patchValue(null);
    this.fileControl.markAsUntouched();
  }

  private getIncoterms(): void {
    this.codeRepository
      .getIncoterms()
      .pipe(takeUntil(this.destroy$))
      .subscribe((incoterms: IncoTermModel[]) => {
        this.incoterms = incoterms;
        this.filterIncoterms();
        this.cdr.markForCheck();
      });
  }

  private filterIncoterms(): void {
    if (this.incoterms && this.incoterms.length && this.transportationMode) {
      this.incotermsByType = this.incoterms.filter((incoterm: IncoTermModel) =>
        this.transportationMode === enumTransportationMode.OCEAN
          ? [enumIncotermApplicableType.ALL, enumIncotermApplicableType.OCEAN].includes(incoterm.applicableTransport)
          : [enumIncotermApplicableType.ALL].includes(incoterm.applicableTransport),
      );
    }
  }

  private removeQueryParams(paramsToRemove: string[]) {
    const queryParams = { ...this.route.snapshot.queryParams };

    paramsToRemove.forEach((param) => {
      delete queryParams[param];
    });

    this.router.navigate([], { queryParams });
  }
}
